import { Effect, Schema, Struct } from 'effect';
import { toNumber } from '../utils/utils.js';
/**
 * Light control model. See docs/plans/light_control.md.
 *
 * A LightController is one physical relay device (today a Shelly Pro). A
 * LightZone is one switchable lighting circuit on that device — a channel —
 * together with the spaces whose bookings should light it.
 *
 * Note the linkage runs zone -> spaces, the reverse of AccessPoint (where the
 * Space points at the access point). Lighting is genuinely many-to-many: one
 * contactor often lights two courts, and a court may need both its floodlights
 * and a shared corridor circuit. The union semantics ("the zone is on if ANY
 * mapped space has an active window") live naturally on the zone.
 *
 * This doc is NOT part of the world-readable site document. It lives at
 * sites/{siteId}/config/lights, which is admin-read-only. Device credentials
 * live in sites/{siteId}/private/devices/{id} and are server-only.
 */
const DEFAULT_LEAD_MINUTES = 5;
const DEFAULT_TRAIL_MINUTES = 5;
// A runaway guard, not a timer: set well above the longest legitimate
// continuous lit period (a full tournament day), so it only ever catches a data
// bug. 16 hours comfortably clears a normal open-till-late day.
const DEFAULT_MAX_ON_MINUTES = 960;
/** Manual overrides are bounded; longer suppression belongs in a season override. */
const MAX_MANUAL_ON_HOURS = 24;
const text = Schema.String.pipe(Schema.withDecodingDefaultType(Effect.succeed('')));
const count = (fallback) => Schema.Number.pipe(Schema.withDecodingDefaultType(Effect.succeed(fallback)));
const LightControllerSchema = Schema.Struct({
    // The converter drops an id-less controller; here it fails the decode.
    id: Schema.NonEmptyString,
    name: text,
    type: Schema.Literals(['shelly', 'gateway']).pipe(Schema.withDecodingDefaultType(Effect.succeed('shelly'))),
    /** Informational, e.g. 'ShellyPro3'. Not used for dispatch. */
    model: text,
    channels: count(1),
    firmwareVersion: text
}).mapFields(Struct.map(Schema.mutableKey));
/** 'MM-DD', or '' for no bound. */
const MonthDay = Schema.String.pipe(Schema.check(Schema.isPattern(/^(\d{2}-\d{2})?$/)));
const LightZoneSchema = Schema.Struct({
    id: Schema.NonEmptyString,
    name: text,
    controllerId: text,
    /** 0-based relay index on the controller. */
    channel: count(0),
    /** Spaces whose bookings light this zone. */
    spaceIds: Schema.mutable(Schema.Array(Schema.NonEmptyString)).pipe(Schema.withDecodingDefaultType(Effect.sync(() => []))),
    /** Lights on this many minutes before the slot starts. */
    leadMinutes: count(DEFAULT_LEAD_MINUTES),
    /** ...and off this many minutes after it ends. */
    trailMinutes: count(DEFAULT_TRAIL_MINUTES),
    /**
     * Sanity ceiling on a single merged window, guarding against a data bug
     * leaving a hall lit for days. 0 means no limit. This is a truncation
     * guard, not a scheduling policy — set it well above the longest legitimate
     * booking (a tournament day) or it will black out a live booking.
     */
    maxOnMinutes: count(DEFAULT_MAX_ON_MINUTES),
    // Absent means enabled: a zone that exists is live unless explicitly paused.
    enabled: Schema.Boolean.pipe(Schema.withDecodingDefaultType(Effect.succeed(true))),
    /**
     * Yearly-recurring "lights off" season, as inclusive 'MM-DD' bounds (e.g.
     * outdoor courts that need no light all summer). Empty strings = no season
     * override. A range where from > to wraps the year end (e.g. 11-01..02-28).
     * While in season, booking-driven windows are suppressed; a manual override
     * still wins. See docs/plans/light_control.md.
     */
    seasonOffFrom: MonthDay.pipe(Schema.withDecodingDefaultType(Effect.succeed(''))),
    seasonOffTo: MonthDay.pipe(Schema.withDecodingDefaultType(Effect.succeed(''))),
    /**
     * Manual force-ON expiry, ISO timestamp. While now < this, the zone is held
     * ON regardless of bookings or season — for testing the automation or lighting
     * a hall for an off-Velpr event. Capped at MAX_MANUAL_ON_HOURS on write; for
     * anything longer use the season override. Empty/past = inactive.
     */
    manualOnUntil: text
}).mapFields(Struct.map(Schema.mutableKey));
const LastSeenSchema = Schema.Record(Schema.String, Schema.String).pipe(Schema.withDecodingDefaultType(Effect.sync(() => ({}))));
/**
 * `sites/{siteId}/config/lights` as stored. Every field is optional on the way
 * in, with the defaults `toLightsDoc` has always applied.
 */
const LightsDocSchema = Schema.Struct({
    controllers: Schema.mutable(Schema.Array(LightControllerSchema)).pipe(Schema.withDecodingDefaultType(Effect.sync(() => []))),
    zones: Schema.mutable(Schema.Array(LightZoneSchema)).pipe(Schema.withDecodingDefaultType(Effect.sync(() => []))),
    /**
     * Legacy: last device check-in per controller. The schedule endpoint used to
     * stamp it here on every poll, and each stamp fired the admin page's snapshot
     * and wiped unsaved edits (D74). Check-ins now go to config/lightsStatus
     * (LightsStatusDoc); this is only read as a fallback for controllers that
     * have not polled since.
     */
    controllerLastSeen: LastSeenSchema,
    version: count(1)
}).mapFields(Struct.map(Schema.mutableKey));
/** Normalize a user-entered month-day to 'MM-DD', or '' when unparseable. */
const toMonthDay = (raw) => {
    if (typeof raw !== 'string')
        return '';
    const match = raw.trim().match(/^(\d{1,2})[-/.](\d{1,2})$/);
    if (!match)
        return '';
    const month = Number(match[1]);
    const day = Number(match[2]);
    if (month < 1 || month > 12 || day < 1 || day > 31)
        return '';
    const pad = (n) => (n < 10 ? '0' + n : '' + n);
    return pad(month) + '-' + pad(day);
};
const emptyLightsDoc = () => ({
    controllers: [],
    zones: [],
    controllerLastSeen: {},
    version: 1
});
const makeLightController = (overrides = {}) => ({
    id: '',
    name: '',
    type: 'shelly',
    model: '',
    channels: 1,
    firmwareVersion: '',
    ...overrides
});
const makeLightZone = (overrides = {}) => ({
    id: '',
    name: '',
    controllerId: '',
    channel: 0,
    spaceIds: [],
    leadMinutes: DEFAULT_LEAD_MINUTES,
    trailMinutes: DEFAULT_TRAIL_MINUTES,
    maxOnMinutes: DEFAULT_MAX_ON_MINUTES,
    enabled: true,
    seasonOffFrom: '',
    seasonOffTo: '',
    manualOnUntil: '',
    ...overrides
});
const toLightControllerType = (raw) => raw === 'gateway' ? 'gateway' : 'shelly';
const toLightController = (raw) => ({
    id: raw?.id ?? '',
    name: raw?.name ?? '',
    type: toLightControllerType(raw?.type),
    model: raw?.model ?? '',
    channels: toNumber(raw?.channels, 1),
    firmwareVersion: raw?.firmwareVersion ?? ''
});
const toLightZone = (raw) => ({
    id: raw?.id ?? '',
    name: raw?.name ?? '',
    controllerId: raw?.controllerId ?? '',
    channel: toNumber(raw?.channel, 0),
    spaceIds: Array.isArray(raw?.spaceIds)
        ? raw.spaceIds.filter((s) => typeof s === 'string' && s !== '')
        : [],
    leadMinutes: toNumber(raw?.leadMinutes, DEFAULT_LEAD_MINUTES),
    trailMinutes: toNumber(raw?.trailMinutes, DEFAULT_TRAIL_MINUTES),
    maxOnMinutes: toNumber(raw?.maxOnMinutes, DEFAULT_MAX_ON_MINUTES),
    // Absent means enabled: a zone that exists is live unless explicitly paused.
    enabled: raw?.enabled !== false,
    seasonOffFrom: toMonthDay(raw?.seasonOffFrom),
    seasonOffTo: toMonthDay(raw?.seasonOffTo),
    manualOnUntil: typeof raw?.manualOnUntil === 'string' ? raw.manualOnUntil : ''
});
const toLightsDoc = (raw) => {
    if (!raw || typeof raw !== 'object')
        return emptyLightsDoc();
    const result = emptyLightsDoc();
    for (const controller of raw.controllers || []) {
        const parsed = toLightController(controller);
        // Drop id-less entries, mirroring toAccessPoints.
        if (parsed.id !== '')
            result.controllers.push(parsed);
    }
    for (const zone of raw.zones || []) {
        const parsed = toLightZone(zone);
        if (parsed.id !== '')
            result.zones.push(parsed);
    }
    result.controllerLastSeen = toLastSeenMap(raw.controllerLastSeen);
    result.version = toNumber(raw.version, 1);
    return result;
};
const toLastSeenMap = (raw) => {
    const result = {};
    if (raw && typeof raw === 'object') {
        for (const [id, seen] of Object.entries(raw)) {
            if (typeof seen === 'string')
                result[id] = seen;
        }
    }
    return result;
};
const toLightsStatusDoc = (raw) => ({
    controllerLastSeen: toLastSeenMap(raw?.controllerLastSeen)
});
const LightsStored = {
    name: 'LightsDoc',
    schema: LightsDocSchema,
    legacy: toLightsDoc,
    authority: 'legacy'
};
/** Last check-in ISO timestamp for a controller, or '' if it has never polled. */
const controllerLastSeenAt = (doc, controllerId) => doc.controllerLastSeen[controllerId] ?? '';
const lightControllerById = (doc, controllerId) => controllerId ? doc.controllers.find((c) => c.id === controllerId) : undefined;
const lightZonesForController = (doc, controllerId) => doc.zones.filter((z) => z.controllerId === controllerId);
const lightZonesForSpace = (doc, spaceId) => doc.zones.filter((z) => z.spaceIds.includes(spaceId));
export { DEFAULT_LEAD_MINUTES, DEFAULT_MAX_ON_MINUTES, DEFAULT_TRAIL_MINUTES, LightsDocSchema, LightsStored, MAX_MANUAL_ON_HOURS, toMonthDay, controllerLastSeenAt, emptyLightsDoc, lightControllerById, lightZonesForController, lightZonesForSpace, makeLightController, makeLightZone, toLightController, toLightZone, toLightsDoc, toLightsStatusDoc };
//# sourceMappingURL=lights.js.map